import { useState, useCallback, useMemo, useEffect } from 'react';
import type { DataGridColumn } from '../../atoms/DataGrid/DataGridAtom';

export interface DataListSettings {
  /** Which columns are visible (by field name) */
  visibleColumns: Record<string, boolean>;
  /** Number of rows per page */
  rowsPerPage: number;
  /** Sort field */
  sortField: string | null;
  /** Sort order */
  sortOrder: 'asc' | 'desc';
}

export interface UseDataListSettingsOptions {
  /** Unique key for localStorage persistence (e.g., "context-inventory") */
  settingsKey: string;
  /** Column definitions to derive default visibility */
  columns: DataGridColumn[];
  /** Default rows per page */
  defaultRowsPerPage?: number;
  /** Default sort field */
  defaultSortField?: string;
  /** Default sort order */
  defaultSortOrder?: 'asc' | 'desc';
}

export interface UseDataListSettingsResult {
  /** Current settings */
  settings: DataListSettings;
  /** Update a single setting */
  updateSetting: <K extends keyof DataListSettings>(key: K, value: DataListSettings[K]) => void;
  /** Toggle column visibility */
  toggleColumnVisibility: (field: string) => void;
  /** Reset to defaults */
  resetSettings: () => void;
  /** Get filtered columns based on visibility settings */
  visibleColumnsList: DataGridColumn[];
  /** Check if a column is visible */
  isColumnVisible: (field: string) => boolean;
}

const STORAGE_PREFIX = 'datalist-settings-';

function getStorageKey(settingsKey: string): string {
  return `${STORAGE_PREFIX}${settingsKey}`;
}

function loadFromStorage(settingsKey: string): Partial<DataListSettings> | null {
  try {
    const stored = localStorage.getItem(getStorageKey(settingsKey));
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.warn('[useDataListSettings] Failed to load settings from localStorage:', e);
  }
  return null;
}

function saveToStorage(settingsKey: string, settings: DataListSettings): void {
  try {
    localStorage.setItem(getStorageKey(settingsKey), JSON.stringify(settings));
  } catch (e) {
    console.warn('[useDataListSettings] Failed to save settings to localStorage:', e);
  }
}

/**
 * Hook for managing DataList settings with localStorage persistence.
 */
export function useDataListSettings({
  settingsKey,
  columns,
  defaultRowsPerPage = 10,
  defaultSortField,
  defaultSortOrder = 'desc',
}: UseDataListSettingsOptions): UseDataListSettingsResult {
  // Build default visibility from columns (all visible by default, except actions)
  const defaultVisibility = useMemo(() => {
    const visibility: Record<string, boolean> = {};
    columns.forEach((col) => {
      // Actions column is always visible and not toggleable
      if (col.field !== 'actions') {
        visibility[col.field] = true;
      }
    });
    return visibility;
  }, [columns]);

  const getDefaultSettings = useCallback((): DataListSettings => ({
    visibleColumns: defaultVisibility,
    rowsPerPage: defaultRowsPerPage,
    sortField: defaultSortField || null,
    sortOrder: defaultSortOrder,
  }), [defaultVisibility, defaultRowsPerPage, defaultSortField, defaultSortOrder]);

  // Initialize state from localStorage or defaults
  const [settings, setSettings] = useState<DataListSettings>(() => {
    const stored = loadFromStorage(settingsKey);
    const defaults = getDefaultSettings();

    if (stored) {
      // Merge stored settings with defaults (in case new columns were added)
      return {
        ...defaults,
        ...stored,
        visibleColumns: {
          ...defaults.visibleColumns,
          ...stored.visibleColumns,
        },
      };
    }

    return defaults;
  });

  // Save to localStorage whenever settings change
  useEffect(() => {
    saveToStorage(settingsKey, settings);
  }, [settingsKey, settings]);

  const updateSetting = useCallback(<K extends keyof DataListSettings>(
    key: K,
    value: DataListSettings[K]
  ) => {
    setSettings((prev) => ({
      ...prev,
      [key]: value,
    }));
  }, []);

  const toggleColumnVisibility = useCallback((field: string) => {
    setSettings((prev) => ({
      ...prev,
      visibleColumns: {
        ...prev.visibleColumns,
        [field]: !prev.visibleColumns[field],
      },
    }));
  }, []);

  const resetSettings = useCallback(() => {
    const defaults = getDefaultSettings();
    setSettings(defaults);
  }, [getDefaultSettings]);

  const isColumnVisible = useCallback((field: string): boolean => {
    // Actions column is always visible
    if (field === 'actions') return true;
    return settings.visibleColumns[field] !== false;
  }, [settings.visibleColumns]);

  const visibleColumnsList = useMemo(() => {
    return columns.filter((col) => isColumnVisible(col.field));
  }, [columns, isColumnVisible]);

  return {
    settings,
    updateSetting,
    toggleColumnVisibility,
    resetSettings,
    visibleColumnsList,
    isColumnVisible,
  };
}

export default useDataListSettings;
